import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUtensils,
  faPlus,
  faMinus,
  faPrint,
  faTimes,
  faSearch,
  faCashRegister,
  faKeyboard,
  faReceipt,
  faCheckCircle,
  faClock,
  faUser,
  faBolt,
  faBackspace,
} from '@fortawesome/free-solid-svg-icons';
import './CreateBill.css';
import { buildApiUrl } from '../../config/api.config';

const CreateBill = () => {

  const location = useLocation();

  // Refs for keyboard navigation
  const foodSearchRef = useRef(null);
  const keypadRef = useRef('');
  const contentRef = useRef(null);

  // Layout State
  const [rightWidth, setRightWidth] = useState(450);

  // State
  const [state, setState] = useState({
    foodsList: [],
    selectedFoods: [],
    foodSearchQuery: '',
    discount: 0,
    paymentMethod: 'cash',
    tableNumber: '',
    notes: '',
    loading: false,
    saving: false,
    printing: false,
    initialLoad: true,
    showKeyboardShortcuts: false,
    
    // User & Reservation Integration
    customerMode: 'walk-in', // 'walk-in' | 'registered'
    users: [],
    selectedUser: null,
    userSearchQuery: '',
    showUserDropdown: false,
    reservations: [],
    selectedReservation: null,
    
    // Keypad state
    keypadMode: 'Qty', // 'Qty', 'Disc', 'Price'
    keypadString: '',
    selectedItemIndex: null
  });

  const startResizing = (e) => {
    e.preventDefault();
    const handleMouseMove = (moveEvent) => {
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        // Calculate new width from the right edge of the container
        const newWidth = rect.right - moveEvent.clientX;
        // Restrict between 320px and 800px
        if (newWidth >= 320 && newWidth <= 800) {
          setRightWidth(newWidth);
        }
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  const calculations = React.useMemo(() => {
    const subtotal = state.selectedFoods.reduce((sum, f) => sum + (f.total || 0), 0);
    
    // 1. Calculate GST on the raw item subtotal (pre-discount)
    const cgst = subtotal * 0.025;
    const sgst = subtotal * 0.025;
    const totalWithGst = subtotal + cgst + sgst;
    
    // 2. Apply discount on the GST-included total
    const discountPercent = Math.max(0, Math.min(100, state.discount || 0));
    const discountAmount = totalWithGst * (discountPercent / 100);
    
    // 3. Final calculations
    const taxableAmount = subtotal; // For record-keeping
    const totalBeforeRoundOff = totalWithGst - discountAmount;
    const roundOff = Math.round(totalBeforeRoundOff) - totalBeforeRoundOff;
    const finalAmount = totalBeforeRoundOff + roundOff;

    return {
      subtotal,
      discount: discountAmount,
      taxableAmount,
      cgst: parseFloat(cgst.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      roundOff: parseFloat(roundOff.toFixed(2)),
      finalAmount: parseFloat(finalAmount.toFixed(2))
    };
  }, [state.selectedFoods, state.discount]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, []);

  // Fetch reservations when a user is selected
  useEffect(() => {
    const fetchReservations = async () => {
      if (!state.selectedUser) {
        setState(prev => ({ ...prev, reservations: [], selectedReservation: null }));
        return;
      }
        const availablePayments = state.selectedUser.payments?.filter(
          p => (p.status === "succeeded" || p.status === "admin-assisted") && !p.deducted
        ) || [];
        const mappedReservations = availablePayments.map(p => ({
          reservationId: p.reservationId || p._id,
          tableNumber: p.tableNumber,
          slotTime: p.slotTime,
          status: p.status
        }));
        setState(prev => ({ ...prev, reservations: mappedReservations, selectedReservation: mappedReservations.length === 1 ? mappedReservations[0] : null }));
    };
    fetchReservations();
  }, [state.selectedUser]);

  // Keyboard shortcuts and barcode scanner
  useEffect(() => {
    const handleKeyPress = (e) => {
      const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
      
      // If user presses Escape while in an input, clear it
      if (isInput && e.key === 'Escape') {
        if (e.target === foodSearchRef.current) {
          setState(prev => ({ ...prev, foodSearchQuery: '' }));
        }
        e.target.blur();
      }

      // Ignore normal character shortcuts if typing in an input
      if (isInput && !e.key.startsWith('F') && e.key !== 'Escape') {
        return;
      }

      switch (e.key) {
        case 'F4': e.preventDefault(); foodSearchRef.current?.focus(); break;
        case 'F9': e.preventDefault(); handleSaveAndPrint(); break;
        case 'F10': e.preventDefault(); handleSave(); break;
        case 'Escape': e.preventDefault(); handleClear(); break;
        case '?': 
          if (!isInput) {
            e.preventDefault(); 
            setState(prev => ({ ...prev, showKeyboardShortcuts: !prev.showKeyboardShortcuts })); 
          }
          break;
        case 'Backspace':
          if (!isInput) {
            e.preventDefault();
            handleKeypadClick('backspace');
          }
          break;
        case 'Enter':
          if (!isInput) {
            e.preventDefault();
            handleKeypadClick('Enter');
          }
          break;
        default:
          if (!isInput && !e.ctrlKey && !e.altKey && !e.metaKey) {
            if (/^[0-9.]$/.test(e.key)) {
              e.preventDefault();
              handleKeypadClick(e.key);
            }
          }
          break;
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const fetchData = async () => {
    try {
      const uDetails = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;
      if (!activeRestaurantId) { toast.error('Restaurant context missing'); setState(prev => ({ ...prev, initialLoad: false })); return; }

      // Fetch Foods
      const foodRes = await axios.get(buildApiUrl(`/food/list?restaurantId=${activeRestaurantId}`));
      
      // Fetch Users
      const token = localStorage.getItem('token');
      const userRes = await axios.get(buildApiUrl(`/users/admin/all-users?restaurantId=${activeRestaurantId}`), { headers: { 'auth-token': token } });
      let usersList = userRes.data || [];

      // Only display customers who currently have an active reservation (requested by user)
      usersList = usersList.filter(u => {
        if (!u.payments || !Array.isArray(u.payments)) return false;
        return u.payments.some(p => (p.status === "succeeded" || p.status === "admin-assisted") && !p.deducted);
      });

      // Check URL for pre-selected user
      const searchParams = new URLSearchParams(location.search);
      const urlUserId = searchParams.get('userId');
      let defaultSelectedUser = null;
      if (urlUserId) {
        defaultSelectedUser = usersList.find(u => u._id === urlUserId) || null;
      }

      setState(prev => ({ 
        ...prev, 
        foodsList: foodRes.data.data || [], 
        users: usersList,
        selectedUser: defaultSelectedUser,
        customerMode: defaultSelectedUser ? 'registered' : 'walk-in',
        initialLoad: false 
      }));
    } catch (error) {
      console.error('Error fetching food items:', error);
      toast.error('Failed to load menu items');
      setState(prev => ({ ...prev, initialLoad: false }));
    }
  };

  const filteredFoods = React.useMemo(() => {
    if (!state.foodSearchQuery) return state.foodsList;
    const query = state.foodSearchQuery.toLowerCase();
    return state.foodsList.filter(food => food.name?.toLowerCase().includes(query));
  }, [state.foodsList, state.foodSearchQuery]);

  const addFoodItem = (food, fromQuickAdd = false) => {
    const existingIndex = state.selectedFoods.findIndex(f => f.foodId === food._id);
    if (existingIndex >= 0) {
      setState(prev => {
        const updated = [...prev.selectedFoods];
        const item = { ...updated[existingIndex] };
        item.quantity += 1;
        item.total = Number(item.price) * item.quantity;
        updated[existingIndex] = item;
        return { 
          ...prev, 
          selectedFoods: updated, 
          foodSearchQuery: '',
          selectedItemIndex: null,
          keypadMode: 'Qty',
          keypadString: ''
        };
      });
    } else {
      setState(prev => ({
        ...prev,
        selectedFoods: [...prev.selectedFoods, { foodId: food._id, name: food.name, price: Number(food.price), quantity: 1, total: Number(food.price) }],
        foodSearchQuery: '',
        selectedItemIndex: null,
        keypadMode: 'Qty',
        keypadString: ''
      }));
    }
  };

  const updateQuantity = (index, delta, e) => {
    if (e) e.stopPropagation();
    setState(prev => {
      const updated = [...prev.selectedFoods];
      const item = { ...updated[index] };
      const newQuantity = Math.max(1, item.quantity + delta);
      item.quantity = newQuantity;
      item.total = item.price * newQuantity;
      updated[index] = item;
      return { 
        ...prev, 
        selectedFoods: updated,
        selectedItemIndex: index,
        keypadMode: 'Qty',
        keypadString: ''
      };
    });
  };

  const removeFoodItem = (index, e) => {
    if (e) e.stopPropagation();
    setState(prev => {
      let newSelectedIndex = prev.selectedItemIndex;
      if (newSelectedIndex === index) {
        newSelectedIndex = null;
      } else if (newSelectedIndex !== null && newSelectedIndex > index) {
        newSelectedIndex -= 1;
      }
      return { 
        ...prev, 
        selectedFoods: prev.selectedFoods.filter((_, i) => i !== index),
        selectedItemIndex: newSelectedIndex,
        keypadString: ''
      };
    });
  };

  const handleKeypadClick = (key) => {
    if (key === 'Enter') {
      const code = keypadRef.current.trim();
      if (code) {
        const foundFood = state.foodsList.find(f => f.itemCode === code);
        if (foundFood) {
          addFoodItem(foundFood);
        } else {
          toast.error(`Item code '${code}' not found`);
        }
      }
      keypadRef.current = '';
      setState(prev => ({ ...prev, keypadString: '' }));
      return;
    }

    setState(prev => {
      let { keypadMode, keypadString, selectedItemIndex, selectedFoods, discount } = prev;

      if (['Qty', 'Disc', 'Price'].includes(key)) {
        let initialStr = '';
        if (key === 'Disc') {
          initialStr = discount ? discount.toString() : '';
        } else if (selectedItemIndex !== null && selectedFoods[selectedItemIndex]) {
          if (key === 'Qty') initialStr = selectedFoods[selectedItemIndex].quantity.toString();
          if (key === 'Price') initialStr = selectedFoods[selectedItemIndex].price.toString();
        }
        keypadRef.current = initialStr;
        return { ...prev, keypadMode: key, keypadString: initialStr };
      }

      if (key === 'backspace') {
        keypadString = keypadString.slice(0, -1);
      } else if (key === '.') {
        if (!keypadString.includes('.')) {
          keypadString += '.';
        }
      } else {
        keypadString += key;
      }

      keypadRef.current = keypadString;

      const numVal = parseFloat(keypadString);
      const val = isNaN(numVal) ? 0 : numVal;

      if (keypadMode === 'Disc') {
        discount = val;
      } else if (selectedItemIndex !== null && selectedFoods[selectedItemIndex]) {
        const updatedFoods = [...selectedFoods];
        const item = { ...updatedFoods[selectedItemIndex] };
        
        if (keypadMode === 'Qty') {
          item.quantity = val;
          item.total = item.price * item.quantity;
        } else if (keypadMode === 'Price') {
          item.price = val;
          item.total = item.price * item.quantity;
        }
        updatedFoods[selectedItemIndex] = item;
        selectedFoods = updatedFoods;
      }

      return { ...prev, keypadString, discount, selectedFoods };
    });
  };

  const handleSave = async () => {
    if (state.selectedFoods.length === 0) { toast.error('Please add at least one item'); return; }
    setState(prev => ({ ...prev, saving: true }));
    try {
      const uDetails = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;

      const payload = {
        restaurantId: activeRestaurantId,
        foods: state.selectedFoods.map(f => ({ foodId: f.foodId, name: f.name, price: f.price, quantity: f.quantity })),
        discount: calculations.discount, tableNumber: state.tableNumber || null,
        paymentMethod: state.paymentMethod, notes: state.notes || null, autoPrint: false,
        userId: state.selectedUser?._id || null,
        reservationIds: (state.selectedReservations || []).map(r => r.reservationId)
      };

      const response = await axios.post(buildApiUrl(`/invoice/quick-create`), payload, { headers: { 'auth-token': localStorage.getItem('token') } });
      toast.success(`Bill #${response.data.invoice.invoiceNumber} created successfully!`);
      setState(prev => ({ ...prev, selectedFoods: [], discount: 0, tableNumber: '', notes: '', saving: false, selectedUser: null, selectedReservations: [], customerMode: 'walk-in' }));
      fetchData(); // Refresh users so the used reservation doesn't show up again
    } catch (error) {
      console.error('Error creating bill:', error);
      toast.error(error.response?.data?.message || 'Failed to create bill');
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  const handleSaveAndPrint = async () => {
    if (state.selectedFoods.length === 0) { toast.error('Please add at least one item'); return; }
    setState(prev => ({ ...prev, saving: true, printing: true }));
    try {
      const uDetails = JSON.parse(localStorage.getItem("userDetails"));
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      let activeRestaurantId = uDetails?.role === 'owner' ? selectedRestId : uDetails?.restaurantId;
      if (activeRestaurantId && typeof activeRestaurantId === 'object') activeRestaurantId = activeRestaurantId._id;

      const payload = {
        restaurantId: activeRestaurantId,
        foods: state.selectedFoods.map(f => ({ foodId: f.foodId, name: f.name, price: f.price, quantity: f.quantity })),
        discount: calculations.discount, tableNumber: state.tableNumber || null,
        paymentMethod: state.paymentMethod, notes: state.notes || null, autoPrint: true,
        userId: state.selectedUser?._id || null,
        reservationIds: (state.selectedReservations || []).map(r => r.reservationId)
      };

      const response = await axios.post(buildApiUrl(`/invoice/quick-create`), payload, { headers: { 'auth-token': localStorage.getItem('token') } });
      toast.success(`Bill #${response.data.invoice.invoiceNumber} created!`);

      if (response.data.printData) {
        const printWindow = window.open('', '', 'width=400,height=600');
        printWindow.document.write(response.data.printData);
        printWindow.document.close();
        printWindow.print();
      }

      setState(prev => ({ ...prev, selectedFoods: [], discount: 0, tableNumber: '', notes: '', saving: false, printing: false, selectedUser: null, selectedReservations: [], customerMode: 'walk-in' }));
      fetchData(); // Refresh users so the used reservation doesn't show up again
    } catch (error) {
      console.error('Error creating bill:', error);
      toast.error(error.response?.data?.message || 'Failed to create bill');
      setState(prev => ({ ...prev, saving: false, printing: false }));
    }
  };

  const handleClear = () => {
    if (state.selectedFoods.length > 0) {
      if (window.confirm('Clear all items and start new bill?')) {
        setState(prev => ({ ...prev, selectedFoods: [], discount: 0, tableNumber: '', notes: '', foodSearchQuery: '' }));
      }
    } else {
      setState(prev => ({ ...prev, foodSearchQuery: '' }));
    }
  };

  if (state.initialLoad) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <main>
          <div className="tf-flex-center" style={{ padding: '10rem 2rem', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="tf-spinner"></div>
            <p style={{ color: 'var(--tf-text-muted)' }}>Loading menu...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Sidebar />
      <main style={{ padding: '1rem 1.5rem', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Page Header */}
        <header className="tf-page-header" style={{ marginBottom: '1rem', flexShrink: 0 }}>
          <div className="tf-header-content">
            <div className="tf-header-title-group">
              <div className="tf-header-icon" style={{ width: '48px', height: '48px', fontSize: '1.2rem' }}>
                <FontAwesomeIcon icon={faCashRegister} />
              </div>
              <div>
                <h1 className="tf-page-title" style={{ fontSize: '1.5rem' }}>Create Bill</h1>
                <p className="tf-page-subtitle" style={{ fontSize: '0.85rem' }}>Unified POS Billing</p>
              </div>
            </div>
            <div className="tf-header-actions">
              <div className="tf-status-chip" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}>
                <FontAwesomeIcon icon={faClock} style={{ color: 'var(--tf-primary)' }} />
                <span>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <button
                className="tf-status-chip qb-shortcut-chip"
                onClick={() => setState(prev => ({ ...prev, showKeyboardShortcuts: true }))}
                title="Keyboard shortcuts (?)"
                style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
              >
                <FontAwesomeIcon icon={faKeyboard} style={{ color: 'var(--tf-primary)' }} />
                <span>Shortcuts</span>
              </button>
            </div>
          </div>
        </header>

        {/* MAIN CONTENT SPLIT */}
        <div className="qb-content" ref={contentRef} style={{ flex: 1, display: 'flex', overflow: 'hidden', gap: '1rem' }}>
        
        {/* LEFT — Catalog Section */}
          <div className="tf-catalog-section" style={{ flex: 1, overflowY: 'auto' }}>
            {/* Search */}
            <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '1.2rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--tf-primary)', fontSize: '1rem' }} />
                <input
                  ref={foodSearchRef}
                  className="tf-input"
                  style={{ paddingLeft: '3rem' }}
                  type="text"
                  placeholder="Search by item name... (F4)"
                  value={state.foodSearchQuery}
                  onChange={(e) => setState(prev => ({ ...prev, foodSearchQuery: e.target.value }))}
                />
              </div>
            </div>

            {/* Catalog Grid */}
            <div className="tf-catalog-grid">
              {filteredFoods.map(food => {
                const isSelected = state.selectedFoods.some(f => f.foodId === food._id);
                return (
                  <div
                    key={food._id}
                    className="tf-product-card"
                    onClick={() => addFoodItem(food)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div style={{ position: 'relative' }}>
                      <img
                        src={buildApiUrl(`/uploads/${food.image}`)}
                        alt={food.name}
                        className="tf-product-image"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/200x200/1e1e1e/888888?text=TastyFlow'; }}
                        style={{ filter: isSelected ? 'grayscale(0.5) opacity(0.5)' : 'none' }}
                      />
                      {isSelected && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', borderRadius: '4px' }}>
                          <FontAwesomeIcon icon={faCheckCircle} size="lg" style={{ color: 'var(--tf-success)' }} />
                        </div>
                      )}
                    </div>
                    <div className="tf-product-details">
                      <div>
                        <h4 className="tf-product-title" style={{ marginBottom: food.itemCode ? '0.2rem' : '0' }}>{food.name}</h4>
                        {food.itemCode && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--tf-text-muted)', display: 'flex', alignItems: 'center', gap: '0.3rem', fontWeight: '600' }}>
                            <FontAwesomeIcon icon={faBolt} style={{ color: 'var(--tf-primary)', opacity: 0.8 }} />
                            <span>Code {food.itemCode}</span>
                          </div>
                        )}
                      </div>
                      <p className="tf-product-price">₹{food.price}</p>
                    </div>
                  </div>
                );
              })}

              {filteredFoods.length === 0 && (
                <div className="tf-empty-state" style={{ gridColumn: '1 / -1' }}>
                  <FontAwesomeIcon icon={faUtensils} size="3x" style={{ opacity: 0.1, marginBottom: '1rem' }} />
                  <p>No items match your search</p>
                </div>
              )}
            </div>
          </div>

        {/* =========================================
            RESIZER
        ========================================= */}
        <div 
          className="qb-resizer" 
          onMouseDown={startResizing}
          title="Drag to resize"
        ></div>

        {/* =========================================
            RIGHT PANEL: CART, KEYPAD, PAYMENT
        ========================================= */}
        <div className="tf-panel-sticky qb-panel qb-right" style={{ width: `${rightWidth}px` }}>
            
            {/* ZONE A: Context & Customer */}
            <div className="qb-zone-a">
              {/* Customer Mode Tabs */}
              <div className="qb-compact-payment-toggle" style={{ marginBottom: '0.8rem' }}>
                <button
                  className={state.customerMode === 'walk-in' ? 'active' : ''}
                  onClick={() => setState(prev => ({ ...prev, customerMode: 'walk-in', selectedUser: null, selectedReservations: [] }))}
                >
                  WALK-IN GUEST
                </button>
                <button
                  className={state.customerMode === 'registered' ? 'active' : ''}
                  onClick={() => setState(prev => ({ ...prev, customerMode: 'registered' }))}
                >
                  REGISTERED CUSTOMER
                </button>
              </div>

              {state.customerMode === 'registered' && (
                <div className="qb-customer-selector">
                  <div className="qb-customer-icon-wrap">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                  <div className="qb-select-wrap">
                    <select
                      className="qb-customer-select"
                      value={state.selectedUser?._id || ''}
                      onChange={(e) => {
                        const user = state.users.find(u => u._id === e.target.value) || null;
                        setState(prev => ({ ...prev, selectedUser: user }));
                      }}
                    >
                      <option value="" disabled>Select Customer</option>
                      {state.users.map(u => (
                        <option key={u._id} value={u._id}>{u.name} ({u.contact})</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Reservation Tabs */}
              {state.reservations.length > 0 && (
                <div style={{ marginTop: state.customerMode === 'registered' ? '1rem' : '0' }}>
                  <div className="qb-compact-payment-toggle" style={{ marginBottom: (state.selectedReservations || []).length > 0 ? '0.8rem' : '0' }}>
                    <button
                      className={(state.selectedReservations || []).length === 0 ? 'active' : ''}
                      onClick={() => setState(prev => ({ ...prev, selectedReservations: [] }))}
                    >
                      DO NOT LINK
                    </button>
                    <button
                      className={(state.selectedReservations || []).length > 0 ? 'active' : ''}
                      onClick={() => {
                        if ((state.selectedReservations || []).length === 0 && state.reservations.length > 0) {
                          const firstRes = state.reservations[0];
                          setState(prev => ({ 
                            ...prev, 
                            selectedReservations: [firstRes],
                            tableNumber: firstRes.tableNumber
                          }));
                        }
                      }}
                    >
                      LINK RESERVATION(S)
                    </button>
                  </div>

                  {(state.selectedReservations || []).length > 0 && (
                    <div className="qb-reservation-multiselect">
                      <div className="qb-reservation-badge">Reserved Tables</div>
                      <div className="qb-reservation-chips">
                        {state.reservations.map(r => {
                          const isSelected = (state.selectedReservations || []).some(sel => sel.reservationId === r.reservationId);
                          return (
                            <button
                              key={r.reservationId}
                              className={`qb-res-chip ${isSelected ? 'active' : ''}`}
                              onClick={() => {
                                setState(prev => {
                                  let newSel = [...(prev.selectedReservations || [])];
                                  if (isSelected) {
                                    newSel = newSel.filter(sel => sel.reservationId !== r.reservationId);
                                  } else {
                                    newSel.push(r);
                                  }
                                  // Auto-set table number to combined selected tables
                                  const combinedTables = newSel.map(s => s.tableNumber).join(', ');
                                  return { 
                                    ...prev, 
                                    selectedReservations: newSel,
                                    tableNumber: combinedTables
                                  };
                                });
                              }}
                            >
                              Table {r.tableNumber} <small>({r.slotTime})</small>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ZONE B: Order List */}
            <div className="qb-zone-b" style={{ flex: 1, overflowY: 'auto' }}>
              {state.selectedFoods.length > 0 ? (
                <div className="qb-order-list">
                  {state.selectedFoods.map((item, index) => (
                    <div 
                      key={`${item.foodId}-${index}`} 
                      className={`qb-order-item-inline ${state.selectedItemIndex === index ? 'selected' : ''}`}
                      onClick={() => setState(prev => {
                        const isDeselecting = prev.selectedItemIndex === index;
                        const newString = isDeselecting ? '' : prev.selectedFoods[index].quantity.toString();
                        keypadRef.current = newString;
                        return {
                          ...prev, 
                          selectedItemIndex: isDeselecting ? null : index, 
                          keypadString: newString, 
                          keypadMode: 'Qty'
                        };
                      })}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="qb-inline-info">
                        <div className="qb-inline-name">{item.name}</div>
                        <div className="qb-inline-price">₹{item.price.toFixed(2)}</div>
                      </div>
                      <div className="qb-inline-controls">
                        <div className="qb-qty-stepper-mini">
                          <button onClick={(e) => updateQuantity(index, -1, e)}><FontAwesomeIcon icon={faMinus} size="xs" /></button>
                          <span>{item.quantity}</span>
                          <button onClick={(e) => updateQuantity(index, 1, e)}><FontAwesomeIcon icon={faPlus} size="xs" /></button>
                        </div>
                        <div className="qb-inline-total">₹{item.total.toFixed(2)}</div>
                        <button className="qb-inline-remove" onClick={(e) => removeFoodItem(index, e)}>
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="qb-empty-cart">
                  <FontAwesomeIcon icon={faReceipt} className="qb-empty-icon" />
                  <p>Your cart is empty</p>
                  <span>Select items from the menu</span>
                </div>
              )}
            </div>

            {/* TOTAL SUMMARY (Odoo style) */}
            <div className="qb-total-summary">
              {state.discount > 0 ? (
                <span className="qb-discount-label">Discount Applied ({state.discount}%)</span>
              ) : (
                <span></span>
              )}
              <h2>Total: ₹{calculations.finalAmount.toFixed(2)}</h2>
            </div>

            {/* ZONE B.5: Keypad */}
            <div className="qb-zone-keypad">
              <div className="qb-keypad-display">
                <span className="qb-keypad-display-label">
                  {state.keypadMode === 'Disc' 
                    ? 'Global Discount %' 
                    : state.selectedItemIndex !== null 
                      ? `Item ${state.keypadMode}` 
                      : 'Item Code'}
                </span>
                <span className={`qb-keypad-display-value ${!state.keypadString ? 'empty' : ''}`}>
                  {state.keypadString || '---'}
                </span>
              </div>
              
              <div className="qb-keypad-with-payment">
                {/* Payment/Order Column */}
                <div className="qb-payment-column">
                  <button className="qb-btn-payment" onClick={handleSaveAndPrint} disabled={state.saving || state.selectedFoods.length === 0}>
                    <FontAwesomeIcon icon={faPrint} size="lg" style={{ marginBottom: '0.2rem' }} />
                    <span>Print</span>
                  </button>
                  <button className={`qb-btn-mode-side ${state.keypadMode === 'Disc' ? 'active' : ''}`} onClick={() => handleKeypadClick('Disc')}>% Disc</button>
                  <button className={`qb-btn-mode-side ${state.keypadMode === 'Price' ? 'active' : ''}`} onClick={() => handleKeypadClick('Price')}>Price</button>
                </div>

                <div className="qb-keypad-container">
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('1')}>1</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('2')}>2</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('3')}>3</button>
                  <button className="qb-keypad-btn qb-btn-backspace" onClick={() => handleKeypadClick('backspace')}>
                    <FontAwesomeIcon icon={faBackspace} />
                  </button>

                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('4')}>4</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('5')}>5</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('6')}>6</button>
                  <button className={`qb-keypad-btn qb-keypad-mode ${state.keypadMode === 'Qty' ? 'active' : ''}`} onClick={() => handleKeypadClick('Qty')}>Qty</button>

                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('7')}>7</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('8')}>8</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('9')}>9</button>
                  <button className="qb-keypad-btn qb-btn-enter" onClick={() => handleKeypadClick('Enter')}>Enter</button>

                  <button className="qb-keypad-btn qb-btn-zero" onClick={() => handleKeypadClick('0')}>0</button>
                  <button className="qb-keypad-btn" onClick={() => handleKeypadClick('.')}>.</button>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Keyboard Shortcuts Modal */}
        {state.showKeyboardShortcuts && (
          <div className="tf-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setState(prev => ({ ...prev, showKeyboardShortcuts: false })); }}>
            <div className="tf-modal" style={{ maxWidth: '420px' }}>
              <button className="tf-modal-close" onClick={() => setState(prev => ({ ...prev, showKeyboardShortcuts: false }))}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
              <div className="tf-modal-body">
                <h3 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  <FontAwesomeIcon icon={faKeyboard} style={{ color: 'var(--tf-primary)' }} />
                  Keyboard Shortcuts
                </h3>
                <div className="qb-shortcuts-list">
                  {[
                    { label: 'Search items', key: 'F4' },
                    { label: 'Scan Code', key: 'Type + Enter' },
                    { label: 'Add item', key: 'Click' },
                    { label: 'Print & Save', key: 'F9' },
                    { label: 'Save Only', key: 'F10' },
                    { label: 'Clear Form', key: 'ESC' },
                    { label: 'Show shortcuts', key: '?' }
                  ].map(({ label, key }) => (
                    <div key={key} className="qb-shortcut-item">
                      <span>{label}</span>
                      <kbd className="qb-kbd">{key}</kbd>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreateBill;
